import React, { useState, useEffect } from "react"
import { useQuery, useQueryClient } from "react-query"
import { fetchDeals, fetchAllDealsForCSV } from "../../services/main.service"
import Paginator from "../composites/paginator"
import WithAuth from "../hoc/with-auth"
import { LayoutWithSidebar } from "../layouts/layout-with-sidebar.tsx"
import {
  Badge,
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Grid,
  GridItem,
  Button,
  Input,
  IconButton,
  Link as Clink,
  useToast,
} from "@chakra-ui/react"
import _ from "lodash"
import BlockedLoader from "../composites/blocked-loader"
import DateRangePicker from "../composites/date-range-picker"
import { AiOutlineSearch } from "react-icons/ai"
import { InputWrapper } from "../composites"
import { ReactSelect } from "../composites/select"
import { SelectSymbols } from "../composites/select-symbols"
import { AiOutlineDownload } from "react-icons/ai"
import App from "../../App"
import { app } from "../../config"
import { formatDate } from "../../utils"
import { getBaseURL } from "../../services/api"

// Import the new helper function
import downloadJsonToCSVHelper from "../../utils/downloadJsonToCSVHelper"
import downloadDealsToCSV from "../../utils/downloadJsonToCSVHelper"

const captions = [
  "Deal",
  "Order",
  "Position",
  "Login",
  "Name",
  "Time",
  "Type",
  "Entry",
  "Symbol",
  "Volume",
  "Price",
  "S / L",
  "T / P",
  "Reason",
  "Commission",
  "Fee",
  "Profit",
  "Dealer",
  "Comment",
]

// Define type for deal object from API
interface DealItem {
  d: string;           // Deal
  o: string;           // Order
  pid: string;         // Position
  l: string;           // Login
  t: string;           // Time
  ac: string;          // Type
  e: string;           // Entry
  s: string;           // Symbol
  v: string;           // Volume
  p: string;           // Price
  sl: string;          // S/L
  tp: string;          // T/P
  r: string;           // Reason
  c: string;           // Commission
  f: string;           // Fee
  pr: string;          // Profit
  de: string;          // Dealer
  co: string;          // Comment
  firstn: string;      // First Name
  lastn: string;       // Last Name
  middlen: string;     // Middle Name
}

// Define the type for the deal array items
type DealItemArray = string | number | null;

// Define API response structure
interface ApiResponse {
  success: boolean;
  code: number;
  message: string;
  data: {
    titles: string[];
    deals: DealItem[];
  };
}

const DealsPage = () => {
  const [isSetForcedUpdate, setIsForcedUpdate] = useState(false)
  const [year, setyear] = useState("2023")
  const [currentPage, setCurrentpage] = useState(1)
  const [isDownloading, setIsDownloading] = useState(false)
  const toast = useToast()

  const [[startDate, endDate], setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([undefined, undefined])

  const [loginId, setLoginId] = useState("")
  const [symbolStr, setSymbolStr] = useState<string>("")
  const [finalState, setFinalState] = useState<{
    year: string
    loginId: string
    symbolStr: string
    startDate: Date | undefined
    endDate: Date | undefined
  }>({
    year: year,
    loginId: loginId,
    symbolStr: symbolStr,
    startDate: startDate,
    endDate: endDate,
  })

  const updateFinalState = () => {
    setFinalState({
      year: year,
      loginId,
      symbolStr,
      startDate,
      endDate,
    })
  }

  const {
    data,
    isLoading,
    isError,
    isRefetching,
    isFetched,
    isFetchedAfterMount,
    refetch,
  } = useQuery(
    [
      "deals",
      {
        currentPage,
        finalState,
      },
    ],
    () =>
      fetchDeals(
        finalState.year,
        currentPage,
        finalState?.startDate ? formatDate(finalState?.startDate) : "",
        finalState?.endDate
          ? formatDate(finalState.endDate)
          : finalState?.startDate
          ? formatDate(finalState?.startDate)
          : "",
        finalState?.loginId,
        finalState?.symbolStr
      ),
    {
      refetchInterval: 4000,
    }
  )

  useEffect(() => {
    if (isFetchedAfterMount) {
      setIsForcedUpdate(true)
    }
  }, [currentPage, year])

  useEffect(() => {
    if (!isFetchedAfterMount) {
      if (!isRefetching) {
        if (isSetForcedUpdate) {
          setIsForcedUpdate(false)
        }
      }
    }
  }, [isRefetching])

// Updated downloadCSV function for DealsPage component
// Updated downloadCSV function for DealsPage component
const downloadCSV = async () => {
  try {
    setIsDownloading(true);
    
    // Fetch all data for CSV with the current filter parameters
    const response = await fetchAllDealsForCSV(
      finalState.year,
      finalState?.startDate ? formatDate(finalState?.startDate) : "",
      finalState?.endDate
        ? formatDate(finalState.endDate)
        : finalState?.startDate
        ? formatDate(finalState?.startDate)
        : "",
      finalState?.loginId,
      finalState?.symbolStr
    );
    
    // Check if we have deals data
    if (!response?.data?.deals || response.data.deals.length === 0) {
      toast({
        title: "No data available",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      setIsDownloading(false);
      return;
    }
    
    // Use our optimized function to download directly from API response
    downloadDealsToCSV(
      response,
      captions,
      `deals-${finalState.year}-${formatDate(finalState.startDate || new Date())}`
    );
    
    toast({
      title: "Download successful",
      description: `${response.data.deals.length} records downloaded`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  } catch (error) {
    console.error("Error downloading CSV:", error);
    toast({
      title: "Download failed",
      description: "There was an error downloading the CSV file",
      status: "error",
      duration: 3000,
      isClosable: true,
    });
  } finally {
    setIsDownloading(false);
  }
};

// Helper function to ensure time is properly formatted for CSV
const formatTimeForCSV = (timeValue: string | number | null): string => {
  if (!timeValue) return '';
  
  // If it's already a formatted date string, return it
  if (typeof timeValue === 'string') {
    // Check if it looks like a date string
    if (timeValue.match(/\d{4}-\d{2}-\d{2}/) || 
        timeValue.match(/\d{2}\/\d{2}\/\d{4}/)) {
      return timeValue;
    }
  }
  
  // If it's a timestamp number
  if (typeof timeValue === 'number') {
    try {
      const date = new Date(timeValue);
      // Check if valid date
      if (!isNaN(date.getTime())) {
        // Format as YYYY-MM-DD HH:MM:SS
        return date.toISOString().replace('T', ' ').split('.')[0];
      }
    } catch (e) {
      // If error, return original
      return String(timeValue);
    }
  }
  
  return String(timeValue);
};

  return (
    <>
      <Grid
        templateColumns={[
          "repeat(1, 1fr)",
          "repeat(2, 1fr)",
          "repeat(3, 1fr)",
          "repeat(4, 1fr)",
          "repeat(5, 1fr)",
        ]}
        gap={6}
      >
        <Box position="relative">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            setDateRange={setDateRange}
          />
        </Box>

        <Box my={4} width="100%">
          <InputWrapper title="Enter Login ID">
            <Input
              value={loginId}
              onChange={(e) => setLoginId(e?.target?.value)}
            />
          </InputWrapper>
        </Box>

        <Box my={4} width="100%">
          <SelectSymbols
            selectedSymbol={symbolStr ?? "ALL"}
            onOptionChange={(_option: string) => {
              setSymbolStr(_option)
            }}
          />
        </Box>

        <Box>
          <Flex align="center">
            <Box pt={[0, "35px"]} pr={4}>
              <Button
                colorScheme="blue"
                onClick={() => {
                  updateFinalState()
                }}
              >
                <Box as={AiOutlineSearch} mr={2} />
                Search
              </Button>
            </Box>

            <Box pt={[0, "35px"]}>
              <Button
                leftIcon={<AiOutlineDownload />}
                colorScheme="green"
                isDisabled={isLoading || isDownloading}
                isLoading={isDownloading}
                loadingText="Downloading..."
                onClick={downloadCSV}
              >
                Download All
              </Button>
            </Box>
          </Flex>
        </Box>
      </Grid>

      <DealsGrid>
        {isLoading ? (
          <Tr>
            <Td colSpan={captions?.length}>
              <BlockedLoader isBlocked={isLoading}>
                <></>
              </BlockedLoader>
            </Td>
          </Tr>
        ) : (
          <>
            {data?.deals?.data?.length ? (
              <>
                {_.map(data?.deals?.data ?? [], (deal) => {
                  return <DealsTableRow key={deal} deal={deal} />
                })}
              </>
            ) : (
              <>
                <Tr>
                  <Td colSpan={captions?.length}>
                    <Box p={4} textAlign="center">
                      <Text color="text.regular">
                        No data found for the selected dates between
                      </Text>
                    </Box>
                  </Td>
                </Tr>
              </>
            )}
          </>
        )}
      </DealsGrid>

      <Box my={4}>
        <Paginator
          currentPage={currentPage}
          totalPages={data?.deals?.last_page ?? 1}
          onChangePage={(_p: number) => setCurrentpage(_p)}
        />
      </Box>
    </>
  )
}

export default DealsPage

interface DealsGridProps {
  children: React.ReactNode;
}

const DealsGrid = ({ children }: DealsGridProps) => {
  return (
    <>
      <Box overflowX="auto">
        <Table variant="striped" color="text.regular" border={1}>
          <Thead>
            <Tr my=".8rem" ps="0px">
              {captions.map((caption, idx) => {
                return (
                  <Th
                    color="gray.400"
                    key={idx}
                    ps={idx === 0 ? "0px" : "0px"}
                    textAlign={caption === "PL" ? "right" : "unset"}
                  >
                    {caption}
                  </Th>
                )
              })}
            </Tr>
          </Thead>
          <Tbody>{children}</Tbody>
        </Table>
      </Box>
    </>
  )
}

interface DealsTableRowProps {
  deal: any[];
}

const DealsTableRow = ({ deal }: DealsTableRowProps) => {
  return (
    <>
      <Tr>
        {_.map(deal, (_d, index) => {
          if (deal?.length === index + 1) return <></>

          return (
            <Td pl={1} pr={2} key={index}>
              <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
                <Text fontSize="md" fontWeight="bold" minWidth="100%">
                  {_d}
                </Text>
              </Flex>
            </Td>
          )
        })}
      </Tr>
    </>
  )
}